/**
 * Data Panel › Seasonality (`docs/plans/seasonality-layout.md`; owner's text
 * mock, 2026-10-23). One window — the row picked on the Seasonality screen, or
 * today for the plan's default horizon — described in every past year: the
 * period profile from its start, each year's own dates and move, and the year ×
 * month table with hit and avg above the years. Pro (`ind:SeasonalityPro`),
 * hidden for everyone else; follows the active chart's symbol.
 */

import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from "react";

import { fetchDetail, type SeasDetail } from "../api/seasonality";
import {
  cellTint,
  onPicked,
  pickedFor,
  signedPct,
  UNIT_LABEL,
  wentItsWay,
} from "../lib/seasonality-detail";
import { shortDate } from "../lib/seasonality-screen";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const YEARS = ["5", "10", "20", "30", "all"] as const;
type Basis = "d" | "w" | "m";

export function SeasonalityPanel({ symbol }: { symbol: string }): ReactElement {
  const [, bump] = useState(0);
  useEffect(() => onPicked(() => bump((n) => n + 1)), []);
  const picked = pickedFor(symbol);
  const [source, setSource] = useState<"picked" | "today">("picked");
  const [years, setYears] = useState<string>(picked?.years ?? "10");
  const [basis, setBasis] = useState<Basis>(picked?.basis ?? "d");
  const usePicked = source === "picked" && picked !== null;

  // A row picked on the screen brings its own years and basis with it.
  useEffect(() => {
    if (!picked) return;
    setSource("picked");
    setYears(picked.years);
    setBasis(picked.basis);
  }, [picked?.symbol, picked?.from, picked?.len, picked?.dir]); // eslint-disable-line react-hooks/exhaustive-deps

  const ask = useMemo(
    () =>
      usePicked
        ? { symbol, basis: picked!.basis, years, from: picked!.from, len: picked!.len, dir: picked!.dir }
        : { symbol, basis, years },
    [symbol, basis, years, usePicked, picked],
  );
  const [detail, setDetail] = useState<SeasDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    setError(null);
    void fetchDetail(ask, abort.signal).then((r) => {
      if (abort.signal.aborted) return;
      if (r.ok) setDetail(r.detail);
      else {
        setDetail(null);
        setError(r.reason);
      }
    });
    return () => abort.abort();
  }, [JSON.stringify(ask)]); // eslint-disable-line react-hooks/exhaustive-deps

  const unit = UNIT_LABEL[detail?.basis ?? basis] ?? "td";
  const tone = (dir: "L" | "S") => (dir === "L" ? "var(--qf-positive)" : "var(--qf-negative)");

  return (
    <div style={ROOT}>
      <div style={HEAD}>
        {detail ? (
          <>
            <b>
              {detail.symbol}{" "}
              <span style={{ color: tone(detail.dir) }}>{detail.dir === "L" ? "LONG" : "SHORT"}</span> ·{" "}
              {shortDate(detail.from)}
              {detail.to ? ` → ${shortDate(detail.to)}` : ""} in each past year
            </b>
            <span style={MUTED}>
              {detail.len}
              {unit} · {detail.stats.up} of {detail.stats.yearsUsed} up
            </span>
          </>
        ) : (
          <b>{symbol}</b>
        )}
      </div>

      <div style={CONTROLS}>
        <label style={LBL}>Window</label>
        <Seg
          value={usePicked ? "picked" : "today"}
          options={[
            ["picked", picked ? `Screen row` : "Screen row", !picked],
            ["today", "Today"],
          ]}
          onChange={(v) => setSource(v as "picked" | "today")}
        />
        <label style={LBL}>Years</label>
        <Seg value={years} options={YEARS.map((y) => [y, y === "all" ? "All" : y])} onChange={setYears} />
        {!usePicked && (
          <>
            <label style={LBL}>Basis</label>
            <Seg value={basis} options={[["d", "D"], ["w", "W"], ["m", "M"]]} onChange={(v) => setBasis(v as Basis)} />
          </>
        )}
      </div>

      {error && <div style={NOTICE}>{error}</div>}
      {!detail && !error && <div style={NOTICE}>Loading…</div>}

      {detail && (
        <div style={SCROLL}>
          <div style={SUB}>
            Period profile <span style={MUTED}>from {shortDate(detail.from)} · bars = hit %, line = avg %</span>
          </div>
          <Profile detail={detail} unit={unit} color={tone(detail.dir)} />

          <div style={SUB}>Each past year</div>
          <div style={YEARS_GRID}>
            {detail.each.map((e) => (
              <div key={e.year} style={YEAR_ROW}>
                <span>
                  {e.year}
                  {e.from && e.to ? ` · ${shortDate(e.from)} → ${shortDate(e.to)}` : ""}
                </span>
                <span style={{ color: wentItsWay(e.ret, detail.dir) ? "var(--qf-positive)" : "var(--qf-negative)" }}>
                  {signedPct(e.ret)}
                </span>
              </div>
            ))}
          </div>

          <div style={SUB}>Year × month, %</div>
          <div style={{ overflowX: "auto", padding: "0 10px 10px" }}>
            <table style={HEAT}>
              <thead>
                <tr>
                  <th />
                  {MONTHS.map((m) => (
                    <th key={m} style={HEAT_TH}>{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* hit and avg sit above the years they summarise (owner, 2026-10-22) */}
                <tr>
                  <th style={HEAT_ROWH}>hit</th>
                  {detail.months.hit.map((h, i) => (
                    <td key={i} style={{ ...HEAT_TD, fontWeight: 700 }}>{h === null ? "" : Math.round(h)}</td>
                  ))}
                </tr>
                <tr style={{ borderBottom: "1px solid var(--qf-border)" }}>
                  <th style={HEAT_ROWH}>avg</th>
                  {detail.months.avg.map((a, i) => (
                    <td key={i} style={{ ...HEAT_TD, color: a === null ? undefined : a >= 0 ? "var(--qf-positive)" : "var(--qf-negative)" }}>
                      {a === null ? "" : a.toFixed(1)}
                    </td>
                  ))}
                </tr>
                {detail.months.years.map((row) => (
                  <tr key={row.year}>
                    <th style={HEAT_ROWH}>{row.year}</th>
                    {row.values.map((v, i) => (
                      <td key={i} style={{ ...HEAT_TD, background: cellTint(v) }}>{v === null ? "" : v.toFixed(1)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** Bars for hit %, a line for avg %, one per length; the window's own length bold. */
function Profile({ detail, unit, color }: { detail: SeasDetail; unit: string; color: string }): ReactElement {
  const W = 560, H = 150, l = 30, r = 12, t = 16, b = 24;
  const n = detail.profile.length || 1;
  const bw = (W - l - r) / n;
  const avgs = detail.profile.map((p) => Math.abs(p.avg));
  const top = Math.max(1, ...avgs) * 1.2;
  const y = (hit: number) => t + (H - t - b) * (1 - hit / 100);
  const ya = (avg: number) => t + (H - t - b) * (1 - (Math.abs(avg) / top));
  let path = "";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img"
      aria-label="Period profile">
      {[0, 50, 100].map((v) => (
        <g key={v}>
          <line x1={l} x2={W - r} y1={y(v)} y2={y(v)} stroke="var(--qf-border)" />
          <text x={l - 4} y={y(v) + 3} fontSize="10" textAnchor="end" fill="var(--qf-text-muted)">{v}</text>
        </g>
      ))}
      {detail.profile.map((p, i) => {
        const x = l + i * bw + bw * 0.2;
        const w = bw * 0.6;
        const own = p.len === detail.len;
        const cx = x + w / 2;
        path += `${i ? "L" : "M"}${cx},${ya(p.avg)}`;
        return (
          <g key={p.len}>
            <rect x={x} y={y(p.hit)} width={w} height={H - b - y(p.hit)} fill={color} opacity={own ? 0.75 : 0.3} rx="2" />
            <text x={cx} y={H - b - 5} fontSize="11" textAnchor="middle" fill="var(--qf-text)" fontWeight={own ? 700 : 400}>
              {Math.round(p.hit)}
            </text>
            <text x={cx} y={H - 8} fontSize="10" textAnchor="middle" fill="var(--qf-text-muted)" fontWeight={own ? 700 : 400}>
              {p.len}{unit}
            </text>
            <circle cx={cx} cy={ya(p.avg)} r="3" fill="var(--qf-warn)" />
            <text x={cx} y={ya(p.avg) - 6} fontSize="10" textAnchor="middle" fill="var(--qf-warn)">
              {signedPct(p.avg)}
            </text>
          </g>
        );
      })}
      <path d={path} fill="none" stroke="var(--qf-warn)" strokeWidth="1.6" />
    </svg>
  );
}

function Seg({
  value, options, onChange,
}: {
  value: string;
  options: readonly (readonly [string, string, boolean?])[];
  onChange: (v: string) => void;
}): ReactElement {
  return (
    <span style={SEG}>
      {options.map(([v, label, disabled], i) => (
        <button key={v} type="button" disabled={disabled} aria-pressed={value === v} onClick={() => onChange(v)}
          style={{ ...SEG_BTN, ...(i ? { borderLeft: "1px solid var(--qf-border)" } : {}), ...(value === v ? SEG_ON : {}),
            ...(disabled ? { opacity: 0.45, cursor: "not-allowed" } : {}) }}>
          {label}
        </button>
      ))}
    </span>
  );
}

const ROOT: CSSProperties = { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, fontSize: 12, color: "var(--qf-text)" };
const HEAD: CSSProperties = { display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 10px", borderBottom: "1px solid var(--qf-border)", flexWrap: "wrap" };
const CONTROLS: CSSProperties = { display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderBottom: "1px solid var(--qf-border)", flexWrap: "wrap" };
const LBL: CSSProperties = { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--qf-text-muted)" };
const MUTED: CSSProperties = { color: "var(--qf-text-muted)" };
const NOTICE: CSSProperties = { padding: 10, color: "var(--qf-text-muted)" };
const SCROLL: CSSProperties = { overflow: "auto", flex: "1 1 auto", minHeight: 0 };
const SUB: CSSProperties = { fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--qf-text-muted)", padding: "8px 10px 2px" };
const YEARS_GRID: CSSProperties = { columns: 2, columnGap: 18, padding: "2px 10px 8px", fontVariantNumeric: "tabular-nums" };
const YEAR_ROW: CSSProperties = { display: "flex", justifyContent: "space-between", breakInside: "avoid", padding: "1px 0" };
const HEAT: CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 10.5, fontVariantNumeric: "tabular-nums" };
const HEAT_TH: CSSProperties = { color: "var(--qf-text-muted)", fontWeight: 600, padding: "2px 3px", textAlign: "center" };
const HEAT_ROWH: CSSProperties = { ...HEAT_TH, textAlign: "left" };
const HEAT_TD: CSSProperties = { padding: "2px 3px", textAlign: "center" };
const SEG: CSSProperties = { display: "inline-flex", border: "1px solid var(--qf-border)", borderRadius: 6, overflow: "hidden" };
const SEG_BTN: CSSProperties = { border: 0, background: "transparent", padding: "2px 8px", font: "inherit", fontSize: 11.5, color: "var(--qf-text)", cursor: "pointer" };
const SEG_ON: CSSProperties = { background: "var(--qf-accent)", color: "var(--qf-on-accent, #fff)" };
